import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { deepLinkToPath } from '../utils/appDeepLink';

/**
 * Solo en el binario nativo: convierte los universal links (iOS) en navegación
 * interna. Cubre el arranque en frío (getLaunchUrl) y la app ya abierta
 * (appUrlOpen). En web no hace nada.
 *
 * Dos cuidados que no son obvios:
 *  · `navigate` cambia de identidad con cada ruta (react-router 6) y
 *    App.getLaunchUrl() devuelve SIEMPRE el último link recibido (Capacitor no
 *    lo limpia). Si el efecto dependiera de `navigate`, cada cambio de ruta
 *    volvería a leer el link viejo y devolvería al cliente al portal — atrapado
 *    hasta cerrar la app. Por eso: el efecto corre UNA vez y `navigate` va en un
 *    ref.
 *  · En frío, la misma URL llega por getLaunchUrl y por appUrlOpen casi a la
 *    vez: se ignora la repetición solo dentro de 2 s. Volver a tocar el mismo
 *    link minutos después (p. ej. tras cerrar sesión) SÍ debe navegar.
 */
const NativeDeepLinks = () => {
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;
    const lastHandledRef = useRef(null);

    useEffect(() => {
        if (!window.Capacitor?.isNativePlatform?.()) return;
        let handle = null;
        let cancelled = false;

        const go = (url) => {
            if (!url) return;
            const last = lastHandledRef.current;
            if (last && last.url === url && Date.now() - last.at < 2000) return;
            const path = deepLinkToPath(url);
            if (!path) return;
            lastHandledRef.current = { url, at: Date.now() };
            navigateRef.current(path, { replace: true });
        };

        CapacitorApp.getLaunchUrl()
            .then(r => { if (!cancelled && r?.url) go(r.url); })
            .catch(() => { /* sin plugin: nada */ });

        CapacitorApp.addListener('appUrlOpen', ({ url }) => { if (!cancelled) go(url); })
            .then(h => { if (cancelled) h.remove?.(); else handle = h; })
            .catch(() => { /* sin plugin: nada */ });

        return () => { cancelled = true; handle?.remove?.(); };
    }, []);

    return null;
};

export default NativeDeepLinks;
